import React from "react";
import { useForm } from "react-hook-form";
import {
  FaUser,
  FaEnvelope,
  FaCalendar,
  FaClock,
  FaHourglassHalf,
  FaMapMarkerAlt,
} from "react-icons/fa";
import CustomInput from "../form/input/CustomInput";
import LabelArea from "../form/Label/LabelArea";
import { AiOutlineUser } from "react-icons/ai";
import CustomSelect from "../../common/CustomSelect";

const EventForm = ({
  register,
  errors,
  handleSubmit,
  onSubmit,
  watch,
  loading,
  exiting,
  handleStatus,
  presonList,
  setSelectedAssistant,
  selectedAssistant,
}) => {
  const statusValue = watch("status", true);
  const userId = watch("id");

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col h-full justify-between"
      style={{ minHeight: "400px" }}
    >
      <div className="flex flex-col gap-5">
        {/* Event Name */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-purple-300 p-2 rounded-full">
                  <FaUser className="text-white text-base" />
                </div>
                <span>Event Name</span>
              </div>
            }
          />
          <CustomInput
            label="Name"
            name="name"
            register={register}
            disabled={!statusValue}
            required={true}
            placeholder="Enter event name"
            errors={errors}
            maxLength={45}
          />
        </div>

        {/* Event Description */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-blue-300 p-2 rounded-full">
                  <FaEnvelope className="text-white text-base" />
                </div>
                <span>Event Description</span>
              </div>
            }
          />
          <textarea
            name="description"
            {...register("description", {
              required: "Description is required",
            })}
            disabled={!statusValue}
            placeholder="Enter event description"
            className={`w-full px-3 py-2 focus:outline-none border rounded-md resize-y min-h-[100px] ${
              errors.description ? "border-red-500" : "border-gray-300"
            }`}
            maxLength={150}
          />
          {errors.description && (
            <p className="text-red-500 text-[11px] mt-1">
              {errors.description.message}
            </p>
          )}
        </div>

        {/* Date */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-green-300 p-2 rounded-full">
                  <FaCalendar className="text-white text-base" />
                </div>
                <span>Date</span>
              </div>
            }
          />
          <CustomInput
            label="Date"
            name="date"
            type="date"
            register={register}
            disabled={exiting}
                required={true}
            placeholder="Select date"
            errors={errors}
          />
        </div>

        {/* Time */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-orange-300 p-2 rounded-full">
                  <FaClock className="text-white text-base" />
                </div>
                <span>Time</span>
              </div>
            }
          />
          <CustomInput
            label="Time"
            name="time"
            type="time"
            register={register}
            disabled={exiting}
            required={true}
            placeholder="Select time"
            errors={errors}
          />
        </div>

        {/* Duration */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-purple-300 p-2 rounded-full">
                  <FaHourglassHalf className="text-white text-base" />
                </div>
                <span>Duration</span>
              </div>
            }
          />
          <div className="flex items-center gap-4">
            <select
              {...register("durationHours", { required: "Hours are required" })}
              disabled={exiting}
              className={`border px-2 h-[40px] w-full rounded-[10px] focus:outline-none ${
                errors.durationHours
                  ? "border-red-500"
                  : "border-slate-200 focus:border-primary"
              } pr-10`}
            >
              <option value="">Select Hour</option>
              {[...Array(13).keys()].map((hour) => (
                <option key={hour} value={hour}>
                  {hour} Hour{hour !== 1 ? "s" : ""}
                </option>
              ))}
            </select>

            <select
              {...register("durationMinutes", {
                required: "Minutes are required",
              })}
              disabled={exiting}
              className={`border px-2 h-[40px] w-full rounded-[10px] focus:outline-none ${
                errors.durationMinutes
                  ? "border-red-500"
                  : "border-slate-200 focus:border-primary"
              } pr-10`}
            >
              <option value="">Select Minutes</option>
              {[0, 15, 30, 45].map((minute) => (
                <option key={minute} value={minute}>
                  {minute} Minute{minute !== 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </div>
          {(errors.durationHours || errors.durationMinutes) && (
            <p className="text-red-500 text-[11px] mt-1">
              {errors.durationHours?.message || errors.durationMinutes?.message}
            </p>
          )}
        </div>

        {/* Set Reminder */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-purple-300 p-2 rounded-full">
                  <FaHourglassHalf className="text-white text-base" />
                </div>
                <span>Set Reminder</span>
              </div>
            }
          />
          <div className="flex items-center gap-4">
            <select
              {...register("setReminder", {
                required: "Set Reminder are required",
              })}
              disabled={exiting}
              className={`border px-2 h-[40px] w-full rounded-[10px] focus:outline-none ${
                errors.setReminder
                  ? "border-red-500"
                  : "border-slate-200 focus:border-primary"
              } pr-10`}
            >
              <option value="">Select Set Reminder</option>
              <option value="30">In 30 minutes</option>
              <option value="60">In 1 hour</option>
              <option value="120">In 2 hours</option>
              <option value="180">In 3 hours</option>
            </select>
          </div>
          {errors.setReminder && (
            <p className="text-red-500 text-[11px] mt-1">
              {errors.setReminder?.message}
            </p>
          )}
        </div>

        {/* Assignee */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-blue-300 p-2 rounded-full">
                  <AiOutlineUser className="text-white text-base" />
                </div>
                <span>Assignee</span>
              </div>
            }
          />
          <CustomSelect
        placeholder="Personal Assistance"
        options={presonList.map((list) => ({
          value: list.id,
          label: list.name,
        }))}
        disabled={exiting}
        value={selectedAssistant}
        onChange={(value) => setSelectedAssistant(value)}
      />
        </div>

        {/* Event Venue */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-red-300 p-2 rounded-full">
                  <FaMapMarkerAlt className="text-white text-base" />
                </div>
                <span>Event Venue</span>
              </div>
            }
          />
          <CustomInput
            label="Venue"
            name="venue"
            register={register}
            disabled={!statusValue}
            required={true}
            placeholder="Enter event venue"
            errors={errors}
            maxLength={100}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        {statusValue && (
          <button
            type="submit"
            disabled={loading}
            className={`${
              loading ? "bg-slate-300" : "bg-primary"
            } w-full text-white py-2 px-4 rounded-[10px]`}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        )}
      </div>
    </form>
  );
};

export default EventForm;
